import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Scrape } from './scrapeDatabase';

/**
 * Get today's date in YYYYMMDD format
 */
export const getTodayVersion = (): string => {
  const today = new Date();
  return format(today, 'yyyyMMdd');
};

/**
 * Run a single scrape task
 */
export const runSingleScrape = async (slug: string, force: boolean, useAI: boolean): Promise<any> => {
  console.log(`🔄 [taskManager] Running scrape for ${slug} (force: ${force}, useAI: ${useAI})`);

  // Prepare the request body
  const requestBody = {
    slug: slug,
    force: force,
    useAI: useAI
  };
  
  console.log(`🔄 [taskManager] Request body:`, requestBody);
  
  // Try alternative approaches if the regular invoke doesn't work
  let response;
  
  try {
    // First attempt: Use the standard invoke method
    console.log(`🔄 [taskManager] Attempting to call screenshot-page with invoke()`);
    
    // Log the Supabase URL to verify it's correct
    const supabaseUrl = supabase.functions.url;
    console.log(`🔄 [taskManager] Supabase functions URL: ${supabaseUrl}`);
    
    response = await supabase.functions.invoke('screenshot-page', {
      body: requestBody
    });
    
    console.log(`🔄 [taskManager] Invoke response:`, response);
    
    if (response.error) {
      throw response.error;
    }
  } catch (invokeError) {
    console.warn(`⚠️ [taskManager] Invoke method failed:`, invokeError);
    
    // Second attempt: Try using the task-dispatcher as a fallback
    console.log(`🔄 [taskManager] Attempting to call task-dispatcher as fallback`);
    
    response = await supabase.functions.invoke('task-dispatcher', {
      body: {
        task: 'scrape',
        ...requestBody
      }
    });
    
    console.log(`🔄 [taskManager] task-dispatcher response:`, response);
    
    if (response.error) {
      throw response.error;
    }
  }
  
  console.log('✅ [taskManager] Scrape job submitted:', response.data);
  return response.data;
};

/**
 * Run scrapes for all pages
 */
export const runBulkScrapes = async (
  type: 'all' | 'category',
  force: boolean,
  useAI: boolean,
  category?: string
): Promise<{ success: boolean; message: string; count?: number }> => {
  console.log(`🔄 [taskManager] Running ${type} pages scrapes (force: ${force}, useAI: ${useAI})`);
  
  // Prepare the request body
  const requestBody: any = {
    force: force,
    useAI: useAI
  };

  if (type === 'all') {
    requestBody.allPages = true;
  } else if (type === 'category' && category) {
    requestBody.categoryPages = true;
    requestBody.category = category;
  }
  
  console.log(`🔄 [taskManager] Request body for ${type} pages:`, requestBody);
  
  // Call the screenshot-page function
  const response = await supabase.functions.invoke('screenshot-page', {
    body: requestBody
  });
  
  console.log(`🔄 [taskManager] ${type} pages scrape response:`, response);
  
  if (response.error) {
    throw response.error;
  }
  
  const result = {
    success: response.data.success,
    message: response.data.message,
    count: response.data.count
  };
  
  console.log(`✅ [taskManager] ${type} pages scrape job submitted:`, response.data);
  return result;
};

/**
 * Verify screenshots for scrapes
 */
export const verifyScreenshots = async (scrapes: Scrape[]): Promise<void> => {
  console.log(`🔄 [taskManager] Verifying screenshots for ${scrapes.length} scrapes`);
  
  // Create verification tasks for each scrape
  for (const scrape of scrapes) {
    const imageUrl = scrape.cloudinary_url || scrape.screenshot_url;
    
    // Prepare the request body for task-enqueue
    const requestBody = {
      type: 'VERIFY_IMAGE',
      slug: scrape.slug,
      version: scrape.version,
      params: {
        imageUrl: imageUrl,
        scrapeId: scrape.id
      }
    };
    
    // Call the task-enqueue function to create a VERIFY_IMAGE task
    await supabase.functions.invoke('task-enqueue', {
      body: requestBody
    });
  }
  
  console.log(`✅ [taskManager] Submitted verification tasks for ${scrapes.length} screenshots`);
};

/**
 * Rerun failed scrapes with force flag - optimized for speed
 */
export const rerunFailedTasks = async (scrapes: Scrape[]): Promise<{ successCount: number; failureCount: number }> => {
  console.log(`🔄 [taskManager] Bulk rerunning ${scrapes.length} scrapes`);
  
  let successCount = 0;
  let failureCount = 0;

  // Process scrapes in larger batches for better performance
  const batchSize = 20;
  for (let i = 0; i < scrapes.length; i += batchSize) {
    const batch = scrapes.slice(i, i + batchSize);
    
    const results = await Promise.allSettled(
      batch.map(async (scrape) => {
        try {
          const requestBody = {
            type: 'SCREENSHOT_PAGE',
            slug: scrape.slug,
            version: getTodayVersion(),
            force: true, // Force flag is always true for reruns (top-level)
            params: {
              useAI: true
            }
          };
          
          const response = await supabase.functions.invoke('task-enqueue', {
            body: requestBody
          });
          
          if (response.error) {
            throw response.error;
          }
          
          return { success: true, slug: scrape.slug };
        } catch (err) {
          return { success: false, slug: scrape.slug, error: err };
        }
      })
    );
    
    // Count results
    results.forEach((result) => {
      if (result.status === 'fulfilled' && result.value.success) {
        successCount++;
      } else {
        failureCount++;
        if (result.status === 'fulfilled' && result.value.error) {
          console.error(`❌ [taskManager] Failed to create task for ${result.value.slug}:`, result.value.error);
        }
      }
    });
    
    // Log progress for large batches
    if (scrapes.length > 10) {
      console.log(`📊 [taskManager] Progress: ${Math.min(i + batchSize, scrapes.length)}/${scrapes.length} processed`);
    }
  }
  
  console.log(`✅ [taskManager] Bulk rerun completed: ${successCount} success, ${failureCount} failed`);
  return { successCount, failureCount };
};

/**
 * Get task status for display
 */
export const getTaskStatus = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'processing':
      return 'default';
    case 'pending':
      return 'secondary';
    default:
      return 'outline';
  }
};

/**
 * Cancel pending tasks (placeholder for future implementation)
 */
export const cancelPendingTasks = async (taskIds: string[]): Promise<void> => {
  // Implementation would go here when needed
  console.log(`🔄 [taskManager] Cancelling ${taskIds.length} pending tasks`);
};

/**
 * Show task result toast notification
 */
export const showTaskResultToast = (
  toast: any,
  type: 'success' | 'error',
  title: string,
  description: string
) => {
  toast({
    variant: type === 'error' ? 'destructive' : 'default',
    title,
    description
  });
};
